import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MarketingLayout } from '@/components/MarketingLayout';
import { Seo } from '@/components/Seo';
import { Reveal } from '@/components/Reveal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowRight, Check, Copy, ShieldCheck, Layers, FileText } from 'lucide-react';

const PROPERTY_TYPES = [
  { value: 'single_small', label: 'Single-family / small residential' },
  { value: 'multifamily', label: 'Mid-size multifamily' },
  { value: 'commercial', label: 'Commercial / mixed-use' },
  { value: 'highrise', label: 'High-rise / high-value asset' },
];

// vendor flags: licensed (trade license), hazard (higher-risk work), drives (auto liability)
const VENDOR_TYPES = [
  { value: 'handyman', label: 'General handyman', licensed: false, hazard: false, drives: false },
  { value: 'cleaner', label: 'Janitorial / cleaning', licensed: false, hazard: false, drives: false },
  { value: 'landscaper', label: 'Landscaping / grounds', licensed: false, hazard: false, drives: true },
  { value: 'plumber', label: 'Plumber', licensed: true, hazard: false, drives: true },
  { value: 'electrician', label: 'Electrician', licensed: true, hazard: true, drives: true },
  { value: 'hvac', label: 'HVAC', licensed: true, hazard: false, drives: true },
  { value: 'roofer', label: 'Roofer', licensed: true, hazard: true, drives: true },
  { value: 'pool', label: 'Pool service', licensed: true, hazard: true, drives: true },
  { value: 'pest', label: 'Pest control', licensed: true, hazard: true, drives: true },
  { value: 'tree', label: 'Tree / arborist', licensed: true, hazard: true, drives: true },
  { value: 'general_contractor', label: 'General contractor', licensed: true, hazard: true, drives: true },
];

function buildRequirements({ property, vendor, hasEmployees }) {
  const v = VENDOR_TYPES.find((x) => x.value === vendor);
  if (!v) return [];

  // General liability limits by property type, escalated for higher-hazard vendors.
  let occ = property === 'commercial' || property === 'highrise' ? 2 : 1;
  if (v.hazard && occ < 2) occ = 2;
  const agg = occ * 2;

  // Umbrella / excess recommendation.
  let umbrella = 0;
  if (property === 'highrise') umbrella = 5;
  else if (v.hazard && (property === 'commercial' || property === 'multifamily')) umbrella = 2;

  const reqs = [
    {
      key: 'gl',
      title: 'Commercial General Liability (COI)',
      detail: `$${occ}M per occurrence / $${agg}M aggregate`,
      required: true,
    },
    {
      key: 'ai',
      title: 'Additional Insured endorsement',
      detail: 'Your entity named as additional insured, primary & non-contributory (CG 20 10 / CG 20 37)',
      required: true,
    },
    {
      key: 'wos',
      title: 'Waiver of Subrogation',
      detail: 'Prevents the insurer from recovering against you after a covered claim',
      required: true,
    },
    {
      key: 'w9',
      title: 'W-9 Tax Form',
      detail: 'Legal name + TIN for 1099 reporting (does not expire)',
      required: true,
    },
  ];

  if (hasEmployees) {
    reqs.push({
      key: 'wc',
      title: "Workers' Compensation",
      detail: 'Statutory limits per your state; required for any vendor with on-site employees',
      required: true,
    });
  }
  if (v.drives) {
    reqs.push({
      key: 'auto',
      title: 'Commercial Auto Liability',
      detail: '$1M combined single limit (vendor operates vehicles on-site)',
      required: true,
    });
  }
  if (v.licensed) {
    reqs.push({
      key: 'license',
      title: 'Trade / Business License',
      detail: 'Current state or local license for the licensed trade',
      required: true,
    });
  }
  if (umbrella) {
    reqs.push({
      key: 'umbrella',
      title: 'Umbrella / Excess Liability',
      detail: `$${umbrella}M excess over the primary policy`,
      required: property === 'highrise',
    });
  }
  return reqs;
}

export default function CoiRequirementBuilderPage() {
  const navigate = useNavigate();
  const [property, setProperty] = useState('multifamily');
  const [vendor, setVendor] = useState('plumber');
  const [hasEmployees, setHasEmployees] = useState(true);

  const reqs = useMemo(
    () => buildRequirements({ property, vendor, hasEmployees }),
    [property, vendor, hasEmployees]
  );

  const propertyLabel = PROPERTY_TYPES.find((p) => p.value === property)?.label;
  const vendorLabel = VENDOR_TYPES.find((v) => v.value === vendor)?.label;

  const copyChecklist = () => {
    const lines = [
      `Insurance & compliance requirements`,
      `Vendor: ${vendorLabel} · Property: ${propertyLabel}`,
      '',
      ...reqs.map((r) => `- ${r.title} — ${r.detail}${r.required ? '' : ' (recommended)'}`),
      '',
      'Guidance only — confirm with your insurance counsel. Generated with VendorSuite.',
    ];
    navigator.clipboard?.writeText(lines.join('\n'));
    toast.success('Requirement checklist copied to clipboard');
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'COI Requirement Builder',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    description:
      'Free tool for property managers to build a recommended vendor insurance requirement checklist by property type and vendor type.',
  };

  return (
    <MarketingLayout>
      <Seo
        title="Free COI Requirement Builder for Property Managers"
        description="Build a recommended vendor insurance requirement checklist in seconds. Choose a property type and vendor type to see the general liability limits, additional-insured endorsements, workers' comp, licenses and more you should require."
        path="/tools/coi-requirement-builder"
        type="website"
        jsonLd={jsonLd}
      />
      <div data-testid="coi-builder-page" className="max-w-4xl mx-auto px-6 py-14">
        <Reveal>
          <Badge variant="secondary" className="mb-4">Free Tool</Badge>
          <h1 className="text-4xl font-bold mb-4 leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            COI Requirement Builder
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl">
            Not sure what to require from a vendor? Pick the property and vendor type and get a recommended insurance &amp;
            compliance checklist you can hand straight to your vendor — built on the same three-layer logic VendorSuite
            uses to score compliance automatically.
          </p>
        </Reveal>

        <div className="grid gap-8 md:grid-cols-5">
          {/* Inputs */}
          <Reveal className="md:col-span-2">
            <div className="rounded-2xl border bg-card p-6 space-y-6 md:sticky md:top-24">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Property type</Label>
                <Select value={property} onValueChange={setProperty}>
                  <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Vendor type</Label>
                <Select value={vendor} onValueChange={setVendor}>
                  <SelectTrigger data-testid="vendor-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VENDOR_TYPES.map((v) => (
                      <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label htmlFor="has-employees" className="text-sm font-medium">Vendor has employees</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Drives the workers' comp requirement</p>
                </div>
                <Switch id="has-employees" data-testid="has-employees-switch" checked={hasEmployees} onCheckedChange={setHasEmployees} />
              </div>
            </div>
          </Reveal>

          {/* Output */}
          <div className="md:col-span-3">
            <Reveal className="mb-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Recommended requirements
                </h2>
                <Button variant="outline" size="sm" data-testid="copy-checklist-button" onClick={copyChecklist}>
                  <Copy className="mr-2 h-4 w-4" /> Copy checklist
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                For a <strong className="text-foreground">{vendorLabel}</strong> working at a{' '}
                <strong className="text-foreground">{propertyLabel}</strong>.
              </p>
            </Reveal>

            <div data-testid="requirements-list" className="space-y-3">
              {reqs.map((r, i) => (
                <Reveal key={r.key} delay={i * 45}>
                  <div className="flex items-start gap-3 rounded-xl border bg-card p-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                      <Check className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{r.title}</span>
                        <Badge variant={r.required ? 'secondary' : 'outline'} className="text-[10px]">
                          {r.required ? 'Required' : 'Recommended'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{r.detail}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <p className="text-xs text-muted-foreground mt-4">
              These are illustrative ranges to help you get started — not legal or insurance advice. Confirm your actual
              requirements with your owners' insurance counsel and any lender or master-policy obligations.
            </p>
          </div>
        </div>

        {/* How it maps to the product */}
        <Reveal className="mt-14">
          <div className="rounded-2xl border bg-muted/30 p-8">
            <span className="inline-flex items-center gap-2 text-primary font-semibold mb-3">
              <Layers className="h-5 w-5" /> This is VendorSuite's three-layer model
            </span>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The checklist above is generated exactly the way VendorSuite scores compliance: a{' '}
              <strong className="text-foreground">vendor-type default</strong>, escalated by a{' '}
              <strong className="text-foreground">property-level override</strong>, with room for{' '}
              <strong className="text-foreground">per-vendor exceptions</strong>. In the app, you define these once and
              every incoming certificate is checked against them automatically — so a vendor can be compliant at your
              duplex and non-compliant at your high-rise, correctly and automatically.
            </p>
          </div>
        </Reveal>

        {/* CTA */}
        <div className="mt-12 rounded-2xl border bg-card p-8 text-center shadow-[var(--shadow-soft)]">
          <ShieldCheck className="h-8 w-8 text-primary mx-auto mb-3" />
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Enforce these requirements automatically
          </h2>
          <p className="text-muted-foreground text-sm mb-5 max-w-lg mx-auto">
            Stop checking certificates by hand. VendorSuite reads every COI, W-9 and license and scores each vendor
            against the exact requirements above — with automated chasing before anything expires.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button data-testid="builder-cta-button" onClick={() => navigate('/signup')}>
              Start Free 30-Day Trial <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button variant="outline" asChild>
              <Link to="/guides/additional-insured-requirements-property-managers">
                <FileText className="mr-2 h-4 w-4" /> Read the additional-insured guide
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
